<template>
  <div class="user-activity">
    <v-gallery :images="images" :index="index" @close="index = null" />
    <v-container>
      <h2 class="mt-5 mb-10">{{$t('activity_report')}}</h2>
      <v-card class="px-6 pb-0 mb-3">
        <v-row>
          <v-col md="3" class="pb-0">
            <v-menu ref="menu" v-model="menu_start" :close-on-content-click="false" transition="scale-transition"
              offset-y min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field :value="dateFormatted(start_date)" :label="$t('start_date')" prepend-icon="mdi-calendar" readonly
                  v-on="on" color="primary"></v-text-field>
              </template>
              <v-date-picker v-model="start_date" @change="onStartDateChange" :locale="$t('locale')" no-title scrollable></v-date-picker>
            </v-menu>
          </v-col>
          <v-col md="3" class="pb-0">
            <v-menu ref="menu" v-model="menu_end" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field :value="dateFormatted(end_date)" :label="$t('end_date')" prepend-icon="mdi-calendar" readonly
                  v-on="on" color="primary"></v-text-field>
              </template>
              <v-date-picker v-model="end_date" @change="onEndDateChange" :locale="$t('locale')" :min="start_date" no-title scrollable>
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col md="3" class="pa-6">
            <v-btn block color="primary" @click="fetchUserActivities()">
              <v-icon>mdi-magnify</v-icon>{{$t('search')}}
            </v-btn>
          </v-col>
          <v-col md="3" class="pa-6">
            <download-excel :data="download" worksheet="Activity" :name="title">
              <v-btn block outlined color="primary" :disabled="download.length < 1">
                <v-icon>mdi-download</v-icon>{{$t('download_report')}}
              </v-btn>
            </download-excel>
          </v-col>
        </v-row>
      </v-card>
      <v-row>
        <v-col md="6">
          <v-text-field :rules="[validator.alfaNumberNotRequired]" :label="$t('find_activity')" prepend-inner-icon="mdi-magnify" single-line
            v-model="search_string" @keyup.enter="fetchUserActivities()"></v-text-field>
        </v-col>
        <v-col md="6">
          <v-select :items="groupList" item-text="alias" item-value="id" :placeholder="$t('search_groups')"
            v-model="searchgroups" @change="fetchUserActivities()"></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-overlay :value="loading">
            <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
          </v-overlay>
          <v-card>
            <v-data-table :loading="loading" 
            :loading-text="$t('loading_activity')"
            :headers="headers"
              :items="activity_list" 
              :server-items-length="totalData"
              :items-per-page="itemPerPage"
              :page="page"
              @update:page="fetchUserActivities($event, itemPerPage)"
              @update:items-per-page="fetchUserActivities(page, $event)"
              :sort-by="['time2']"
              :footer-props="{ 'items-per-page-options': [10, 50, 100, 1000], 'items-per-page-text': $t('rows_per_page') }" 
              :sort-desc="[false, false]" 
              :group-desc="false"
              group-by="date"
              :no-data-text="$t('no_data')" 
              multi-sort>
               <template v-slot:footer.page-text="items"> {{ items.pageStart }} - {{ items.pageStop }} {{$t('of')}} {{ items.itemsLength }} </template>
              <template v-slot:group.header="{ items }">
                <th colspan="100%">
                  <span class="center subtitle-1">
                    <b class="f-13">{{$t('date')}} {{ items[0].date }}</b>
                  </span>
                </th>
              </template>
              <template v-slot:item.details="{ item }">
                <v-icon medium class="mr-2" color="primary" @click="openDetailsDialog(item)">
                  mdi-card-account-details-outline</v-icon>
              </template>
              <template v-slot:top>
                <v-dialog v-model="detailsDialog" max-width="1000">
                  <v-card v-if="loadingDetails" class="pt-3" style="padding: 15px; overflow: hidden;">
                    <v-container>
                      <v-row>
                        <v-col md="6">
                          <v-row>
                            <v-col md="12">
                              <h4> {{$t('activity_details')}}</h4>
                            </v-col>
                            <v-col md="12">
                              <label>
                                {{$t('name')}}
                              </label>
                              <p>{{ userDetail.full_name }}</p>
                            </v-col>
                            <v-col md="12" class="minus-top">
                              <label>
                                {{$t('job_position')}}
                              </label>
                              <p>{{ userDetail.job_position }}</p>
                            </v-col>
                            <v-col md="12" class="minus-top">
                              <label>
                                {{$t('group')}}
                              </label>
                              <p>{{ userDetail.name_group }}</p>
                            </v-col>
                            <v-col md="12" class="minus-top">
                              <label>
                                {{$t('date')}}
                              </label>
                              <p>{{ userDetail.date + ', ' + userDetail.time }}</p>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col md="6">
                          <v-row>
                            <v-col md="12" style="height: 250px">
                              <iframe
                                :src="'https://maps.google.com/maps?q='+userDetail.latitude+','+userDetail.longitude+'&z=15&output=embed'"
                                width="100%" height="100%" frameBorder="0"></iframe>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col md="12" class="minus-top">
                          <label>
                            {{$t('description')}}
                          </label>
                          <p>{{ userDetail.description }}</p>
                        </v-col>
                        <v-col md="12" class="minus-top">
                          <label>
                            {{$t('location')}}
                          </label>
                          <p>{{ userDetail.location }}</p>
                        </v-col>
                        <v-col md="12" class="minus-top">
                          <label>
                            {{$t('attachment')}}
                          </label>
                        </v-col>
                        <v-col v-for="(img, i) in userDetail.images" :key="i" md="2" class="minus-10-top" @click="
                                index = i;
                                images = userDetail.images;" style="margin-right: 60px;">
                          <div class="attachment-images">
                            <img :src="img" style="width: 100%" height="100%">
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </v-dialog>
              </template>
              <template v-slot:footer.page-text="items"> {{ items.pageStart }} - {{ items.pageStop }} {{$t('of')}} {{ items.itemsLength }} </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
  import moment from "moment";  

  export default {
    name: "user-activity",
    data() {
      return {
        download: [],
        openLocation: false,
        activity_list: [],
        user_id: "",
        company_id: this.$store.state.company,
        // downloadName: `Activity Report (${this.$store.state.companyName}) ${this.$store.state.companyName + this.endDate}.xls`,
        user_activities: [],
        user_list: [],
        date: new Date().toISOString().substr(0, 10),
        menu_start: false,
        menu_end: false,
        loading: true,
        loadingDetails: false,
        index: null,
        images: [],
        search: '',
        totalData: 0,
        page: 1,
        itemPerPage: 10,
        userDetail: [],
        search_string: "",
        detailsDialog: false,
        validator: this.$store.state.validator,
        baseUrl: this.$store.state.baseUrl,
        userId: this.$store.state.user.id,
        start_date: "",
        end_date: "",
        groupList: [],
        searchgroups: null,
        headers: [{
            text: this.$i18n.t('date'),
            value: "date",
            width: "10%"
          },
          {
            text: this.$i18n.t('employee_name'),
            value: "full_name",
            width: "20%"
          },
          {
            text: this.$i18n.t('title'),
            value: 'title'
          },
          {
            text: this.$i18n.t('description'),
            value: 'description'
          },
          {
            text: this.$i18n.t('time'),
            value: 'time'
          },
          {
            text: this.$i18n.t('location'),
            value: 'location'
          },
          {
            text: this.$i18n.t('details'),
            value: "details"
          },

        ],
      };
    },
    methods: {
      getLocation(data) {
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${data.latitude},${
          data.longitude
        }`
        );
      },
      getDetailLocation() {
        this.openLocation = !this.openLocation
      },
      openDetailsDialog(item) {
        this.openLocation = false;
        this.loadingDetails = true;
        this.userDetail = item;
        this.detailsDialog = true;
      },
      onStartDateChange() {
        const diff = moment(this.end_date).diff(this.start_date, 'days')
        if (diff < 0) this.end_date = this.start_date
        this.menu_start = false;
      },
      onEndDateChange() {
        this.menu_end = false;
      },
      dateFormatted(date) {
        return moment(date).locale(this.$i18n.t('lang')).format("Do MMMM YYYY");
      },
      convertDate(milis) {
        return moment(milis, "x").format("MMMM Do YYYY, HH:mm");
      },
      milisToDate(milis) {
        return moment(milis, "x").format("DD-MM-YYYY");
      },
      milisToHour(milis) {
        return moment(milis, "x").format("HH:mm:ss");
      },
      milisToHour2(milis) {
        return moment(milis, "x").format("HH:mm");
      },
      onDateChange() {
        this.menu = false;
        this.date_search = moment(this.date, "YYYY-MM-DD").format("DD-MM-YYYY");
        this.fetchUserActivities();
      },
      async fetchUserActivities(page = 1, itemPerPage = this.itemPerPage) {
        this.loading = true
        this.page = page
        this.itemPerPage = itemPerPage
        const totalPageSend = itemPerPage < 1 ? this.totalData : itemPerPage;
        const start = this.page ;
        let date_start = moment(this.start_date).format("DD-MM-YYYY");
        let date_end = moment(this.end_date).format("DD-MM-YYYY");
        const params = {
          page:start,
          pageSize:totalPageSend,
          userId:this.userId,
          startDate:date_start,
          endDate:date_end
        }
        if (this.search_string) params.userName = this.search_string
        if (this.searchgroups) params.groupId = this.searchgroups
        const result = await this.$store.dispatch('getReportActivity', params)
        this.totalData = result.total
        this.user_activities = result.docs;
        this.download = [];
        this.activity_list = [];
        this.user_activities.map((item) => {
          let report_activity = {};
          (report_activity[this.$i18n.t('employee_id')] = item.employee_code),
          (report_activity[this.$i18n.t('full_name')] = item.user_name),
          (report_activity[this.$i18n.t('date')] = this.milisToDate(item.time)),
          (report_activity[this.$i18n.t('time')] = this.milisToHour(item.time)),
          (report_activity[this.$i18n.t('title')] = item.title),
          (report_activity[this.$i18n.t('description')] = item.description),
          (report_activity[this.$i18n.t('location')] = item.location),
          (report_activity[this.$i18n.t('latitude')] = `'${item.latitude}`),
          (report_activity[this.$i18n.t('longitude')] = `'${item.longitude}`),
          // (report_activity["Images"] = item.attached_image[0]), //removed but can be enable later, previously was a typo on calling the response.
          this.download.push(report_activity);
        });
        this.user_activities.map((item) => {
          let foo = {};
          (foo["full_name"] = item.user_name),
          (foo["date"] = this.milisToDate(item.time)),
          (foo["time"] = this.milisToHour2(item.time)),
          (foo["time2"] = item.time),
          (foo["title"] = item.title),
          (foo["job_position"] = item.job_position),
          (foo["description"] = item.description),
          (foo["location"] = item.location),
          (foo["latitude"] = `${item.latitude}`),
          (foo["longitude"] = `${item.longitude}`),
          (foo["name_group"] = item.name_group),
          (foo["images"] = item.attached_image),
          this.activity_list.push(foo);
        });
        this.loading = false
      },
      async fetchGroupDetails() {
        const result = await this.$store.dispatch('getGroupByCompany', { companyId: this.company_id })
        this.groupList = result;
        this.groupList.unshift({ alias: this.$i18n.t('all'), id: null });
      },
    },
    computed: {
      title() {
        let selectedgroup = ''
        if(!this.searchgroups){
            selectedgroup =  this.$i18n.t('all')
        }
        return `Activity Report - ${this.$store.state.companyName
        } (${moment(this.start_date).locale(this.$i18n.t('lang')).format("DD-MM-YYYY")} - ${moment(
          this.end_date
        ).format("DD-MM-YYYY")} - ${selectedgroup}).xls`;
      },
    },
    created() {
      this.start_date = moment()
        .subtract(1, "days")
        .format("YYYY-MM-DD");
      this.end_date = moment().format("YYYY-MM-DD");
      this.fetchUserActivities();
      this.fetchGroupDetails();
    },
  };
</script>
<style scoped>
  label {
    font-size: 14px;
    color: #858585;
    font-weight: bold;
    margin-top: -15px;
  }

  .minus-top {
    margin-top: -30px;
  }

  .minus-10-top {
    margin-top: -20px;
  }

  p {
    font-size: 12px;
  }

  .user-activity {
    min-height: 100vh;
  }

  .attachment-images {
    height: 180px;
    width: 200px;
    background: #C4C4C4;
  }

  .attachment-images:hover {
    cursor: pointer;
  }

  td {
    width: 1px;
    height: 1px;
  }

  .blueimp-gallery {
    background: rgba(0, 0, 0, 0.3) !important;
  }
</style>